// Web Bluetooth API types (not in standard TypeScript lib)
interface BluetoothDevice extends EventTarget {
  id: string;
  name?: string;
  gatt?: BluetoothRemoteGATTServer | null;
  addEventListener(type: "gattserverdisconnected", listener: () => void): void;
}

interface BluetoothRemoteGATTServer {
  device: BluetoothDevice;
  connected: boolean;
  connect(): Promise<BluetoothRemoteGATTServer>;
  disconnect(): void;
  getPrimaryService(service: BluetoothServiceUUID): Promise<BluetoothRemoteGATTService>;
}

interface BluetoothRemoteGATTService extends EventTarget {
  device: BluetoothDevice;
  uuid: string;
  isPrimary: boolean;
  getCharacteristic(characteristic: BluetoothCharacteristicUUID): Promise<BluetoothRemoteGATTCharacteristic>;
}

interface BluetoothRemoteGATTCharacteristic extends EventTarget {
  service: BluetoothRemoteGATTService;
  uuid: string;
  properties: BluetoothCharacteristicProperties;
  writeValue(value: BufferSource): Promise<void>;
  writeValueWithoutResponse?(value: BufferSource): Promise<void>;
}

interface BluetoothCharacteristicProperties {
  broadcast: boolean;
  read: boolean;
  writeWithoutResponse: boolean;
  write: boolean;
  notify: boolean;
  indicate: boolean;
  authenticatedSignedWrites: boolean;
  reliableWrite: boolean;
  writableAuxiliaries: boolean;
}

type BluetoothServiceUUID = number | string;
type BluetoothCharacteristicUUID = number | string;

// Type for navigator.bluetooth (Web Bluetooth API)
interface BluetoothNavigator {
  requestDevice(options: {
    filters: Array<{ namePrefix?: string }>;
    optionalServices?: BluetoothServiceUUID[];
  }): Promise<BluetoothDevice>;
}

class BluetoothHelper {
  private device: BluetoothDevice | null = null;
  private server: BluetoothRemoteGATTServer | null = null;
  private characteristic: BluetoothRemoteGATTCharacteristic | null = null;

  async connect(
    serviceUuid = "0000ffe0-0000-1000-8000-00805f9b34fb",
    characteristicUuid = "0000ffe1-0000-1000-8000-00805f9b34fb",
    namePrefix = "HC-08"
  ) {
    try {
      const bluetooth = (navigator as any).bluetooth as BluetoothNavigator | undefined;
      if (!bluetooth) {
        throw new Error("Web Bluetooth API is not supported in this browser");
      }
      console.log("Requesting Bluetooth device...");
      this.device = await bluetooth.requestDevice({
        filters: [{ namePrefix }],
        optionalServices: [serviceUuid],
      });

      this.device.addEventListener("gattserverdisconnected", this.onDisconnect);

      if (!this.device.gatt) {
        throw new Error("GATT server not available");
      }
      this.server = await this.device.gatt.connect();
      const service = await this.server.getPrimaryService(serviceUuid);
      this.characteristic = await service.getCharacteristic(characteristicUuid);

      console.log(`Connected to ${this.device.name}`);
      return { connected: true, name: this.device.name };
    } catch (err) {
      console.error("Bluetooth connection failed:", err);
      throw err;
    }
  }

  async disconnect() {
    if (this.device?.gatt?.connected) {
      this.device.gatt.disconnect();
      console.log("Device disconnected");
    }
    this.device = null;
    this.server = null;
    this.characteristic = null;
  }

  async sendString(command: string): Promise<boolean> {
    if (!this.characteristic) {
      console.error("No characteristic available for sendString");
      return false;
    }

    const text = command.endsWith("\r\n") ? command : command + "\r\n";
    const encoder = new TextEncoder();
    const data = encoder.encode(text);

    try {
      if ((this.characteristic as any).writeValueWithoutResponse) {
        await (this.characteristic as any).writeValueWithoutResponse(data);
      } else {
        await this.characteristic.writeValue(data);
      }
      console.log("Sent to device:", JSON.stringify(text));
      return true;
    } catch (err) {
      console.error("Failed to sendString:", command, err);
      return false;
    }
  }

  isConnected(): boolean {
    return !!this.device && !!this.device.gatt?.connected;
  }

  getDeviceName(): string | null {
    return this.device?.name || null;
  }

  private onDisconnect = () => {
    console.warn("Bluetooth device disconnected");
    this.device = null;
    this.server = null;
    this.characteristic = null;
  };
}

export const bluetoothHelper = new BluetoothHelper();
